import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import AIAgentOrb from 'src/components/AIAgentOrb';
import { DesktopLayout } from 'src/layout';
import AIManagerService, {
    BillingSummary,
    ConversationTimelineItem,
    KnowledgeDocument,
    ManagerAccess,
    ManagerAttachment,
    ManagerConversation,
    ManagerGoalTemplate,
    ManagerIntakeOptions,
    ManagerIntakeSourceMode,
    ManagerLeadSelection,
    ManagerOutbound,
    ManagerProfile,
    TestDialogLimits,
    TestMessage,
} from 'src/services/AIManagerService';

import DialogPackageCheckout from './DialogPackageCheckout';

const cardSx = {
    border: '1px solid var(--lm-border)',
    borderRadius: '10px',
    boxShadow: 'none',
    bgcolor: 'var(--lm-surface)',
} as const;
const fieldSx = {
    '& .MuiInputLabel-root': {
        color: 'var(--lm-muted)',
        '&.Mui-focused': { color: 'var(--lm-text)' },
    },
    '& .MuiFormHelperText-root': { color: 'var(--lm-muted)' },
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        bgcolor: 'var(--lm-surface)',
        color: 'var(--lm-text)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--lm-border)' },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--lm-muted)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--lm-accent-ink)',
        },
    },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
        height: 'auto',
        padding: 0,
        alignItems: 'flex-start',
    },
} as const;
type Section = 'list' | 'wizard' | 'launch' | 'dialogs';
type TelegramAuthStage = 'phone' | 'code' | 'password';
type TelegramConnectionMode = 'existing' | 'new';

const wizardSteps = [
    { title: 'Первое сообщение', caption: 'Что менеджер напишет сразу' },
    { title: 'Цель', caption: 'Какого результата добиться' },
    { title: 'О компании', caption: 'Что нужно знать о вас' },
    { title: 'Правила и материалы', caption: 'Правила и база знаний' },
] as const;

const goalTemplates: Array<{
    value: ManagerGoalTemplate;
    label: string;
    goal: string;
    success: string;
    handoff: string;
}> = [
    {
        value: 'handoff',
        label: 'Передать заинтересованного клиента',
        goal: 'Выявить интерес и передать клиента сотруднику.',
        success: 'Клиент подтвердил интерес и готов продолжить с сотрудником.',
        handoff: 'Клиент заинтересован или просит связать со специалистом.',
    },
    {
        value: 'consultation',
        label: 'Записать на консультацию',
        goal: 'Согласовать удобное время для консультации и передать клиента сотруднику.',
        success: 'Клиент назвал удобное время для консультации.',
        handoff: 'Время согласовано или клиент просит сотрудника.',
    },
    {
        value: 'application',
        label: 'Собрать заявку',
        goal: 'Собрать контакты и ключевые детали запроса.',
        success: 'Получены контакты и данные, нужные для обработки заявки.',
        handoff: 'Заявка собрана или клиент просит помощь сотрудника.',
    },
    {
        value: 'questions',
        label: 'Ответить на вопросы',
        goal: 'Понятно ответить на вопросы клиента по материалам компании.',
        success: 'Клиент получил ответ и понимает следующий шаг.',
        handoff: 'В базе знаний нет точного ответа или клиент просит сотрудника.',
    },
    {
        value: 'custom',
        label: 'Своя цель',
        goal: '',
        success: '',
        handoff: '',
    },
];

const terminalTelegramAuthErrors = new Set([
    'new_code_required',
    'session_not_found',
    'session_expired',
    'code_expired',
    'too_many_attempts',
    'telegram_verify_failed',
    'telegram_timeout',
    'telegram_handoff_failed',
    'account_in_use',
]);

const blockerLabels: Record<string, string> = {
    lead_stream_missing: 'Не выбран поток лидов',
    telegram_not_connected: 'Не подключён ваш Telegram',
    setup_test_missing: 'Не принят тестовый диалог',
    setup_test_context_changed: 'После изменений нужно заново принять тест',
    setup_test_failed: 'Тестовый диалог не принят',
    dialog_credits_empty: 'Нет доступных диалогов',
    source_in_use_by_manager:
        'Источник уже использует другой запущенный менеджер. Выберите другой или поставьте того менеджера на паузу',
    transport_unavailable: 'Отправка сообщений временно недоступна',
    transport_check_failed: 'Отправка сообщений временно недоступна',
    transport_check_invalid: 'Отправка сообщений временно недоступна',
    telegram_client_not_live: 'Ожидается подключение Telegram',
    live_client_not_authorized: 'Ожидается подключение Telegram',
    private_peer_missing: 'Контакт автора недоступен',
    private_peer_invalid: 'Контакт автора недоступен',
    private_author_identity_missing: 'Контакт автора недоступен',
    private_author_identity_invalid: 'Контакт автора недоступен',
    private_author_username_missing: 'Контакт автора недоступен',
    private_author_content_changed: 'Лид изменился. Обновите диалог',
    source_content_changed: 'Лид изменился. Обновите диалог',
    delivery_unknown_requires_reconciliation: 'Статус доставки неизвестен. Проверьте диалог вручную',
};

function blockerLabel(code: string) {
    if (blockerLabels[code]) return blockerLabels[code];
    if (code.startsWith('transport_') || code.startsWith('private_')) {
        return 'Отправка сообщений временно недоступна';
    }
    if (code.startsWith('setup_test_')) return 'Примите актуальный тестовый диалог';
    return 'Проверьте настройки менеджера';
}

function errorMessage(error: unknown, fallback: string) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (typeof data === 'string') {
        if (blockerLabels[data]) return blockerLabel(data);
        return /^[a-z0-9_:-]+$/i.test(data) ? fallback : data;
    }
    if (data && typeof data === 'object') {
        const first = Object.values(data as Record<string, unknown>)[0];
        if (typeof (data as Record<string, unknown>).detail === 'string') {
            const detail = String((data as Record<string, unknown>).detail);
            if (
                detail.startsWith('transport_') ||
                detail.startsWith('private_') ||
                detail.startsWith('setup_test_') ||
                blockerLabels[detail]
            ) {
                return blockerLabel(detail);
            }
            return /^[a-z0-9_:-]+$/i.test(detail) ? fallback : detail;
        }
        if (typeof first === 'string') {
            if (blockerLabels[first]) return blockerLabel(first);
            return /^[a-z0-9_:-]+$/i.test(first) ? fallback : first;
        }
        if (Array.isArray(first) && first[0]) {
            const message = String(first[0]);
            if (blockerLabels[message]) return blockerLabel(message);
            return /^[a-z0-9_:-]+$/i.test(message) ? fallback : message;
        }
    }
    return fallback;
}

function testDialogLimitsFromError(error: unknown): TestDialogLimits | null {
    const limits = (error as { response?: { data?: { limits?: unknown } } })?.response?.data?.limits;
    if (!limits || typeof limits !== 'object') return null;
    const value = limits as Partial<TestDialogLimits>;
    return value.session && value.daily ? (value as TestDialogLimits) : null;
}

function testDialogErrorCode(error: unknown) {
    return String((error as { response?: { data?: { code?: unknown } } })?.response?.data?.code || '');
}

function telegramAuthErrorCode(error: unknown) {
    return String((error as { response?: { data?: { code?: unknown } } })?.response?.data?.code || '');
}

const telegramOwnershipBlockers = new Set([
    'not_owned',
    'not_linked',
    'no_assignment',
    'telegram_not_connected',
    'telegram_client_not_linked',
    'telegram_client_not_owned',
    'telegram_assignment_missing',
    'telegram_client_assignment_missing',
]);

function telegramLabel(value?: string, transportCode?: string, compact = false) {
    if (transportCode && telegramOwnershipBlockers.has(transportCode)) {
        return compact ? 'Не подключён' : 'Нужен ваш Telegram';
    }
    if (value === 'ready' || value === 'connected') return 'Подключён';
    if (value === 'warning') return 'Нужна проверка';
    return 'Не подключён';
}

function numericSelection(value: unknown): number[] {
    const values = Array.isArray(value) ? value : String(value || '').split(',');
    return Array.from(new Set(values.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0)));
}

type ProfileSaveScope = 'identity' | 'sources' | 'scenario' | 'schedule' | 'opening' | 'goal' | 'company' | 'scripts';
type ProfileFormErrors = Record<string, string>;
type ProfilePayloadResult = {
    payload: Partial<ManagerProfile> | null;
    errors: ProfileFormErrors;
};

const sourceScopeFields: Array<keyof ManagerProfile> = [
    'source_mode',
    'lead_selection',
    'lead_stream_ids',
    'subscription_project_ids',
];
const openingScopeFields: Array<keyof ManagerProfile> = ['name', 'opening_message'];
const goalScopeFields: Array<keyof ManagerProfile> = [
    'goal_template',
    'goal_description',
    'success_criteria',
    'handoff_rule',
];
const companyScopeFields: Array<keyof ManagerProfile> = ['description'];
const advancedScenarioScopeFields: Array<keyof ManagerProfile> = [
    'flow_steps',
    'handoff_rule',
    'success_criteria',
    'rejection_criteria',
    'data_to_collect',
    'stop_phrases',
];
const scheduleScopeFields: Array<keyof ManagerProfile> = [
    'auto_send_private_default',
    'working_timezone',
    'working_days',
    'working_hours_start',
    'working_hours_end',
    'reply_delay_min_seconds',
    'reply_delay_max_seconds',
    'max_new_conversations_per_day',
    'max_outbound_messages_per_hour',
    'max_messages_per_dialog',
    'no_reply_followup_enabled',
    'no_reply_followup_delay_hours',
    'no_reply_followup_text',
];

export const workingDayOptions = [
    ['mon', 'Пн'],
    ['tue', 'Вт'],
    ['wed', 'Ср'],
    ['thu', 'Чт'],
    ['fri', 'Пт'],
    ['sat', 'Сб'],
    ['sun', 'Вс'],
] as const;

const profileValueEquals = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

const inactiveFollowupFields = new Set<keyof ManagerProfile>([
    'no_reply_followup_delay_hours',
    'no_reply_followup_text',
]);

export function hasEffectiveUnsavedProfileChanges(
    dirtyFields: Iterable<keyof ManagerProfile>,
    draft: Partial<ManagerProfile>
) {
    return Array.from(dirtyFields).some(
        (key) => Boolean(draft.no_reply_followup_enabled) || !inactiveFollowupFields.has(key)
    );
}

export function shouldShowSubscriptionAiWarning(
    intakeOptions: ManagerIntakeOptions | undefined,
    sourceMode: ManagerIntakeSourceMode,
    leadSelection: ManagerLeadSelection,
    selectedProjectIds: number[]
) {
    if (
        (sourceMode !== 'subscription' && sourceMode !== 'both') ||
        leadSelection !== 'target_only' ||
        selectedProjectIds.length === 0
    ) {
        return false;
    }
    const selectedProjects = (intakeOptions?.subscription_projects || []).filter((project) =>
        selectedProjectIds.includes(project.id)
    );
    return (
        selectedProjects.length > 0 &&
        selectedProjects.every((project) => !project.ai_analysis_enabled || !project.target_only_available)
    );
}

export function profileDraftFromServer(serverProfile: ManagerProfile): ManagerProfile {
    const intake = serverProfile.intake_options;
    return {
        ...serverProfile,
        source_mode: serverProfile.source_mode ?? intake?.source_mode,
        lead_selection: serverProfile.lead_selection ?? intake?.lead_selection,
        lead_stream_ids: serverProfile.lead_stream_ids ?? intake?.selected.lead_stream_ids ?? [],
        subscription_project_ids:
            serverProfile.subscription_project_ids ?? intake?.selected.subscription_project_ids ?? [],
    };
}

export function filterGrantedProfiles(profiles: ManagerProfile[], access: ManagerAccess) {
    return Array.isArray(access.profile_ids)
        ? profiles.filter((manager) => access.profile_ids?.includes(manager.id))
        : profiles;
}

const commonTimezones = [
    'Europe/Moscow',
    'Europe/Kaliningrad',
    'Europe/Samara',
    'Asia/Yekaterinburg',
    'Asia/Omsk',
    'Asia/Novosibirsk',
    'Asia/Irkutsk',
    'Asia/Yakutsk',
    'Asia/Vladivostok',
    'Asia/Magadan',
    'Asia/Kamchatka',
    'UTC',
];

export function mergeProfileDraft(
    serverProfile: ManagerProfile,
    currentDraft: Partial<ManagerProfile>,
    dirtyFields: Array<keyof ManagerProfile>
): Partial<ManagerProfile> {
    const merged: Partial<ManagerProfile> = { ...serverProfile };
    dirtyFields.forEach((key) => {
        (merged as Record<string, unknown>)[key] = currentDraft[key];
    });
    return merged;
}

export function mergeSavedProfileScope(
    serverProfile: ManagerProfile,
    currentDraft: Partial<ManagerProfile>,
    savedFields: Array<keyof ManagerProfile>,
    dirtyFields: Array<keyof ManagerProfile>
): Partial<ManagerProfile> {
    const saved = new Set(savedFields);
    const merged = mergeProfileDraft(
        serverProfile,
        currentDraft,
        dirtyFields.filter((key) => !saved.has(key))
    );
    savedFields.forEach((key) => {
        (merged as Record<string, unknown>)[key] = serverProfile[key];
    });
    return merged;
}

export function buildIdentityPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const name = String(draft.name ?? '').trim();
    const errors: ProfileFormErrors = {};
    if (!name) errors.name = 'Укажите имя менеджера.';
    return {
        payload: Object.keys(errors).length ? null : { name, description: String(draft.description ?? '').trim() },
        errors,
    };
}

export function buildOpeningPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const name = String(draft.name ?? '').trim();
    const openingMessage = String(draft.opening_message ?? '').trim();
    const errors: ProfileFormErrors = {};
    if (!openingMessage) errors.opening_message = 'Напишите первое сообщение.';
    else if (openingMessage.length > 1000) errors.opening_message = 'Первое сообщение: не больше 1000 символов.';
    if (!name) errors.name = 'Укажите имя менеджера.';
    else if (name.length > 255) errors.name = 'Имя: не больше 255 символов.';
    return {
        payload: Object.keys(errors).length ? null : { name, opening_message: openingMessage },
        errors,
    };
}

export function buildGoalPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const goalDescription = String(draft.goal_description ?? '').trim();
    const errors: ProfileFormErrors = {};
    if (!goalDescription) errors.goal_description = 'Опишите ожидаемый результат.';
    return {
        payload: Object.keys(errors).length
            ? null
            : {
                  goal_template: draft.goal_template || 'custom',
                  goal_description: goalDescription,
                  success_criteria: String(draft.success_criteria ?? '').trim(),
                  handoff_rule: String(draft.handoff_rule ?? '').trim(),
              },
        errors,
    };
}

export function buildCompanyPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const description = String(draft.description ?? '').trim();
    const errors: ProfileFormErrors = {};
    if (!description) errors.description = 'Расскажите о компании и продукте.';
    return {
        payload: Object.keys(errors).length ? null : { description },
        errors,
    };
}

export function buildSourcesPayload(
    sourceMode: ManagerIntakeSourceMode,
    leadSelection: ManagerLeadSelection,
    leadStreamIds: number[],
    subscriptionProjectIds: number[]
): ProfilePayloadResult {
    const errors: ProfileFormErrors = {};
    if ((sourceMode === 'packages' || sourceMode === 'both') && leadStreamIds.length === 0) {
        errors.lead_stream_ids = 'Выберите хотя бы один поток лидов.';
    }
    if ((sourceMode === 'subscription' || sourceMode === 'both') && subscriptionProjectIds.length === 0) {
        errors.subscription_project_ids = 'Выберите хотя бы один проект подписки.';
    }
    return {
        payload: Object.keys(errors).length
            ? null
            : {
                  source_mode: sourceMode,
                  lead_selection: leadSelection,
                  lead_stream_ids: leadStreamIds,
                  subscription_project_ids: subscriptionProjectIds,
              },
        errors,
    };
}

const normalizedLines = (value: string[] | undefined) =>
    (value || []).map((item) => String(item).trim()).filter(Boolean);

function validatedTextList(
    value: string[] | undefined,
    key: keyof ManagerProfile,
    maximumItems: number,
    maximumItemLength: number,
    errors: ProfileFormErrors
) {
    const lines = normalizedLines(value);
    if (lines.length > maximumItems || lines.some((line) => line.length > maximumItemLength)) {
        errors[key] = `Не больше ${maximumItems} строк, до ${maximumItemLength} символов в каждой.`;
    }
    return lines;
}

export function buildScenarioPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const goalDescription = String(draft.goal_description ?? '').trim();
    const errors: ProfileFormErrors = {};
    if (!goalDescription) errors.goal_description = 'Опишите цель диалога.';
    const flowSteps = validatedTextList(draft.flow_steps, 'flow_steps', 20, 500, errors);
    const dataToCollect = validatedTextList(draft.data_to_collect, 'data_to_collect', 50, 128, errors);
    const stopPhrases = validatedTextList(draft.stop_phrases, 'stop_phrases', 100, 255, errors);
    return {
        payload: Object.keys(errors).length
            ? null
            : {
                  goal_description: goalDescription,
                  goal_template: draft.goal_template || 'custom',
                  opening_message: String(draft.opening_message ?? '').trim(),
                  flow_steps: flowSteps,
                  handoff_rule: String(draft.handoff_rule ?? '').trim(),
                  success_criteria: String(draft.success_criteria ?? '').trim(),
                  rejection_criteria: String(draft.rejection_criteria ?? '').trim(),
                  data_to_collect: dataToCollect,
                  stop_phrases: stopPhrases,
              },
        errors,
    };
}

export function buildScriptsPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const errors: ProfileFormErrors = {};
    const flowSteps = validatedTextList(draft.flow_steps, 'flow_steps', 20, 500, errors);
    return {
        payload: Object.keys(errors).length ? null : { flow_steps: flowSteps },
        errors,
    };
}

export function buildAdvancedScenarioPayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const errors: ProfileFormErrors = {};
    const flowSteps = validatedTextList(draft.flow_steps, 'flow_steps', 20, 500, errors);
    const dataToCollect = validatedTextList(draft.data_to_collect, 'data_to_collect', 50, 128, errors);
    const stopPhrases = validatedTextList(draft.stop_phrases, 'stop_phrases', 100, 255, errors);
    return {
        payload: Object.keys(errors).length
            ? null
            : {
                  flow_steps: flowSteps,
                  handoff_rule: String(draft.handoff_rule ?? '').trim(),
                  success_criteria: String(draft.success_criteria ?? '').trim(),
                  rejection_criteria: String(draft.rejection_criteria ?? '').trim(),
                  data_to_collect: dataToCollect,
                  stop_phrases: stopPhrases,
              },
        errors,
    };
}

function validatedInteger(
    draft: Partial<ManagerProfile>,
    key: keyof ManagerProfile,
    label: string,
    minimum: number,
    maximum: number,
    errors: ProfileFormErrors
) {
    const rawValue = draft[key];
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
        errors[key] = `Укажите значение «${label}».`;
        return null;
    }
    const value = Number(rawValue);
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
        errors[key] = `Допустимое значение: целое число от ${minimum} до ${maximum}.`;
        return null;
    }
    return value;
}

export function buildSchedulePayload(draft: Partial<ManagerProfile>): ProfilePayloadResult {
    const errors: ProfileFormErrors = {};
    const replyMin = validatedInteger(draft, 'reply_delay_min_seconds', 'Мин. задержка, сек', 0, 2147483647, errors);
    const replyMax = validatedInteger(draft, 'reply_delay_max_seconds', 'Макс. задержка, сек', 0, 2147483647, errors);
    const conversationsPerDay = validatedInteger(
        draft,
        'max_new_conversations_per_day',
        'Новых диалогов в день',
        30,
        50,
        errors
    );
    const messagesPerHour = validatedInteger(draft, 'max_outbound_messages_per_hour', 'Сообщений в час', 1, 60, errors);
    const messagesPerDialog = validatedInteger(draft, 'max_messages_per_dialog', 'Сообщений в диалоге', 1, 300, errors);
    if (replyMin !== null && replyMax !== null && replyMax < replyMin) {
        errors.reply_delay_max_seconds = 'Максимальная задержка должна быть не меньше минимальной.';
    }

    const timezone = String(draft.working_timezone ?? '').trim();
    if (!timezone) errors.working_timezone = 'Укажите часовой пояс IANA.';
    const start = String(draft.working_hours_start ?? '');
    const end = String(draft.working_hours_end ?? '');
    if (!start) errors.working_hours_start = 'Укажите начало рабочего окна.';
    if (!end) errors.working_hours_end = 'Укажите окончание рабочего окна.';
    if (start && end && start >= end) {
        errors.working_hours_end = 'Окончание рабочего окна должно быть позже начала.';
    }

    const followupEnabled = Boolean(draft.no_reply_followup_enabled);
    let followupDelay: number | null = null;
    const followupText = String(draft.no_reply_followup_text ?? '').trim();
    if (followupEnabled) {
        followupDelay = validatedInteger(draft, 'no_reply_followup_delay_hours', 'Через, часов', 1, 168, errors);
        if (!followupText) errors.no_reply_followup_text = 'Укажите текст напоминания.';
    }

    if (Object.keys(errors).length) return { payload: null, errors };
    const payload: Partial<ManagerProfile> = {
        auto_send_private_default: Boolean(draft.auto_send_private_default),
        working_timezone: timezone,
        working_days: Array.isArray(draft.working_days) ? draft.working_days : [],
        working_hours_start: start,
        working_hours_end: end,
        reply_delay_min_seconds: replyMin as number,
        reply_delay_max_seconds: replyMax as number,
        max_new_conversations_per_day: conversationsPerDay as number,
        max_outbound_messages_per_hour: messagesPerHour as number,
        max_messages_per_dialog: messagesPerDialog as number,
        no_reply_followup_enabled: followupEnabled,
    };
    if (followupEnabled) {
        payload.no_reply_followup_delay_hours = followupDelay as number;
        payload.no_reply_followup_text = followupText;
    }
    return { payload, errors };
}

export default function AIManagerScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const [access, setAccess] = useState<ManagerAccess | null>(null);
    const [managerProfiles, setManagerProfiles] = useState<ManagerProfile[]>([]);
    const [profile, setProfile] = useState<ManagerProfile | null>(null);
    const [draft, setDraft] = useState<Partial<ManagerProfile>>({});
    const [billing, setBilling] = useState<BillingSummary | null>(null);
    const [billingDialogOpen, setBillingDialogOpen] = useState(
        () =>
            typeof window !== 'undefined' &&
            (new URLSearchParams(window.location.search).get('payment') === 'pending' ||
                new URLSearchParams(window.location.search).get('payment_success') === 'true')
    );
    const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
    const [attachments, setAttachments] = useState<ManagerAttachment[]>([]);
    const [conversations, setConversations] = useState<ManagerConversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<ManagerConversation | null>(null);
    const [timeline, setTimeline] = useState<ConversationTimelineItem[]>([]);
    const [outbounds, setOutbounds] = useState<ManagerOutbound[]>([]);
    const [section, setSection] = useState<Section>('list');
    const [wizardStep, setWizardStep] = useState(0);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showAttachments, setShowAttachments] = useState(false);
    const [pendingSelection, setPendingSelection] = useState<number | 'list' | null>(null);
    const wizardTopRef = useRef<HTMLDivElement | null>(null);
    const dirtyProfileFields = useRef<Set<keyof ManagerProfile>>(new Set());
    const canonicalProfileDraft = useRef<Partial<ManagerProfile>>({});
    const loadRequest = useRef(0);
    const createIdempotencyKey = useRef<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [working, setWorking] = useState(false);
    const [profileSaveErrors, setProfileSaveErrors] = useState<ProfileFormErrors>({});
    const [profileSaveErrorScope, setProfileSaveErrorScope] = useState<ProfileSaveScope | null>(null);
    const [knowledgeTitle, setKnowledgeTitle] = useState('Правила общения');
    const [knowledgeBody, setKnowledgeBody] = useState('');
    const [knowledgeDraftError, setKnowledgeDraftError] = useState(false);
    const [editingKnowledgeId, setEditingKnowledgeId] = useState<number | null>(null);
    const [editingKnowledgeTitle, setEditingKnowledgeTitle] = useState('');
    const [editingKnowledgeBody, setEditingKnowledgeBody] = useState('');
    const [attachmentTitle, setAttachmentTitle] = useState('');
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [attachmentCaption, setAttachmentCaption] = useState('');
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
    const [testInput, setTestInput] = useState('');
    const [testMessages, setTestMessages] = useState<TestMessage[]>([]);
    const [testSessionId, setTestSessionId] = useState<number | null>(null);
    const [testSessionState, setTestSessionState] = useState<Record<string, unknown> | null>(null);
    const [testLimits, setTestLimits] = useState<TestDialogLimits | null>(null);
    const [testLimitsUnavailable, setTestLimitsUnavailable] = useState(false);
    const [testRating, setTestRating] = useState(8);
    const [handoffNote, setHandoffNote] = useState('');
    const [outboundDrafts, setOutboundDrafts] = useState<Record<number, string>>({});
    const [telegramConnectionMode, setTelegramConnectionMode] = useState<TelegramConnectionMode>('new');
    const [selectedTelegramClientId, setSelectedTelegramClientId] = useState<number | ''>('');
    const [phone, setPhone] = useState('');
    const [telegramToken, setTelegramToken] = useState('');
    const [telegramCode, setTelegramCode] = useState('');
    const [telegramPassword, setTelegramPassword] = useState('');
    const [telegramStage, setTelegramStage] = useState<TelegramAuthStage>('phone');
    const [telegramAuthNotice, setTelegramAuthNotice] = useState('');
    const telegramAccounts = access?.workspace?.telegram_accounts || [];
    const intakeOptions = profile?.intake_options;
    const sourceMode = (draft.source_mode || intakeOptions?.source_mode || 'packages') as ManagerIntakeSourceMode;
    const leadSelection = (draft.lead_selection ||
        intakeOptions?.lead_selection ||
        'target_only') as ManagerLeadSelection;
    const selectedLeadStreamIds = draft.lead_stream_ids || intakeOptions?.selected.lead_stream_ids || [];
    const selectedSubscriptionProjectIds =
        draft.subscription_project_ids || intakeOptions?.selected.subscription_project_ids || [];
    const showSubscriptionAiWarning = shouldShowSubscriptionAiWarning(
        intakeOptions,
        sourceMode,
        leadSelection,
        selectedSubscriptionProjectIds
    );
    const timezoneOptions = Array.from(
        new Set(
            [
                ...commonTimezones,
                typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
                String(draft.working_timezone ?? ''),
            ].filter(Boolean)
        )
    );
    const hasUnsavedProfileChanges = hasEffectiveUnsavedProfileChanges(dirtyProfileFields.current, draft);
    const hasUnsavedManagerChanges =
        hasUnsavedProfileChanges ||
        Boolean(
            knowledgeBody.trim() ||
                editingKnowledgeId ||
                attachmentTitle.trim() ||
                attachmentUrl.trim() ||
                attachmentCaption.trim() ||
                attachmentFile
        );

    const clearProfileState = useCallback(() => {
        setProfile(null);
        setDraft({});
        setDocuments([]);
        setAttachments([]);
        setConversations([]);
        setSelectedConversation(null);
        setTimeline([]);
        setOutbounds([]);
        setTestMessages([]);
        setTestInput('');
        setTestSessionId(null);
        setTestSessionState(null);
        setTestLimits(null);
        setPhone('');
        setTelegramToken('');
        setTelegramCode('');
        setTelegramPassword('');
        setTelegramStage('phone');
        setTelegramAuthNotice('');
        setProfileSaveErrors({});
        setProfileSaveErrorScope(null);
        setKnowledgeTitle('Правила общения');
        setKnowledgeBody('');
        setKnowledgeDraftError(false);
        setEditingKnowledgeId(null);
        setEditingKnowledgeTitle('');
        setEditingKnowledgeBody('');
        setAttachmentTitle('');
        setAttachmentUrl('');
        setAttachmentCaption('');
        setAttachmentFile(null);
        setTestRating(8);
        setHandoffNote('');
        setOutboundDrafts({});
        setTelegramConnectionMode('new');
        setSelectedTelegramClientId('');
        setShowAdvanced(false);
        setShowAttachments(false);
        dirtyProfileFields.current.clear();
        canonicalProfileDraft.current = {};
    }, []);

    const loadGranted = useCallback(
        async (
            profileId: number,
            currentAccess: ManagerAccess,
            preserveDraft = false,
            requestId = loadRequest.current
        ) => {
            const [profiles, summary] = await Promise.all([
                AIManagerService.listProfiles(),
                AIManagerService.getBilling(),
            ]);
            if (requestId !== loadRequest.current) return;
            const visibleProfiles = filterGrantedProfiles(profiles, currentAccess);
            const current = visibleProfiles.find((manager) => manager.id === profileId) || null;
            setManagerProfiles(visibleProfiles);
            setBilling(summary);
            if (!current) {
                throw new Error('Выданный AI-менеджер недоступен. Обновите страницу или обратитесь в поддержку.');
            }
            const [knowledge, managerAttachments, managerConversations] = await Promise.all([
                AIManagerService.listKnowledge(current.id),
                AIManagerService.listAttachments(current.id),
                AIManagerService.listConversations(current.id),
            ]);
            if (requestId !== loadRequest.current) return;
            setProfile(current);
            const serverDraft = profileDraftFromServer(current);
            if (!preserveDraft) {
                dirtyProfileFields.current.clear();
                canonicalProfileDraft.current = serverDraft;
                setDraft(serverDraft);
            } else {
                setDraft((currentDraft) => {
                    const dirtyFields = Array.from(dirtyProfileFields.current).filter(
                        (key) => !profileValueEquals(currentDraft[key], serverDraft[key])
                    );
                    dirtyProfileFields.current = new Set(dirtyFields);
                    canonicalProfileDraft.current = serverDraft;
                    return mergeProfileDraft(serverDraft, currentDraft, dirtyFields);
                });
            }
            setDocuments(knowledge);
            setAttachments(managerAttachments);
            setConversations(managerConversations);
        },
        []
    );

    const loadList = useCallback(async () => {
        const requestId = ++loadRequest.current;
        setLoading(true);
        setLoadFailed(false);
        clearProfileState();
        try {
            const current = await AIManagerService.getAccess(null);
            if (requestId !== loadRequest.current) return;
            setAccess(current);
            if (current.state === 'granted') {
                const [profiles, summary] = await Promise.all([
                    AIManagerService.listProfiles(),
                    AIManagerService.getBilling(),
                ]);
                if (requestId !== loadRequest.current) return;
                setManagerProfiles(filterGrantedProfiles(profiles, current));
                setBilling(summary);
            } else {
                setManagerProfiles([]);
            }
            setSection('list');
        } catch (error) {
            if (requestId !== loadRequest.current) return;
            setLoadFailed(true);
            enqueueSnackbar(errorMessage(error, 'Не удалось загрузить AI-менеджеров'), { variant: 'error' });
        } finally {
            if (requestId === loadRequest.current) setLoading(false);
        }
    }, [clearProfileState, enqueueSnackbar]);

    const openProfile = useCallback(
        async (profileId: number) => {
            const requestId = ++loadRequest.current;
            setLoading(true);
            setLoadFailed(false);
            clearProfileState();
            setWizardStep(0);
            setSection('wizard');
            try {
                const current = await AIManagerService.getAccess(profileId);
                if (requestId !== loadRequest.current) return;
                setAccess(current);
                if (current.state !== 'granted') throw new Error('Доступ к AI-менеджеру не открыт.');
                await loadGranted(profileId, current, false, requestId);
            } catch (error) {
                if (requestId !== loadRequest.current) return;
                setLoadFailed(true);
                enqueueSnackbar(errorMessage(error, 'Не удалось открыть AI-менеджера'), { variant: 'error' });
            } finally {
                if (requestId === loadRequest.current) setLoading(false);
            }
        },
        [clearProfileState, enqueueSnackbar, loadGranted]
    );

    useEffect(() => {
        const profileId = Number(new URLSearchParams(window.location.search).get('profile_id'));
        if (Number.isSafeInteger(profileId) && profileId > 0) void openProfile(profileId);
        else void loadList();
    }, [loadList, openProfile]);

    useEffect(() => {
        if (section === 'wizard') {
            wizardTopRef.current?.scrollIntoView({
                block: 'start',
                behavior: 'auto',
            });
        }
    }, [section, wizardStep]);

    useEffect(() => {
        if (!profile?.id) return undefined;
        let cancelled = false;
        setTestLimitsUnavailable(false);
        void AIManagerService.getTestDialogLimits(profile.id, testSessionId)
            .then((limits) => {
                if (!cancelled) setTestLimits(limits);
            })
            .catch(() => {
                if (!cancelled) setTestLimitsUnavailable(true);
            });
        return () => {
            cancelled = true;
        };
    }, [profile?.id, testSessionId]);

    useEffect(() => {
        if (!profile) return;
        const accounts = access?.workspace?.telegram_accounts || [];
        setTelegramConnectionMode(accounts.length ? 'existing' : 'new');
        setSelectedTelegramClientId(profile.telegram_client ?? accounts[0]?.id ?? '');
    }, [access?.workspace?.telegram_accounts, profile?.id, profile?.telegram_client]);

    const requestAccess = async () => {
        setWorking(true);
        try {
            const current = await AIManagerService.requestAccess();
            setAccess(current);
            if (current.state === 'granted') await loadList();
            enqueueSnackbar(current.state === 'granted' ? 'Доступ уже открыт' : 'Заявка отправлена', {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось отправить заявку'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const resetTest = () => {
        setTestMessages([]);
        setTestInput('');
        setTestSessionId(null);
        setTestSessionState(null);
        setTestLimits((current) =>
            current
                ? {
                      ...current,
                      session: {
                          limit: current.session.limit,
                          used: 0,
                          remaining: current.session.limit,
                      },
                  }
                : current
        );
    };

    const refreshGranted = async () => {
        if (!profile) return;
        const current = await AIManagerService.getAccess(profile.id);
        setAccess(current);
        await loadGranted(profile.id, current, true);
    };

    const saveProfileScope = async (
        scope: ProfileSaveScope,
        result: ProfilePayloadResult,
        savedFields: Array<keyof ManagerProfile>,
        successMessage: string
    ) => {
        setProfileSaveErrorScope(scope);
        setProfileSaveErrors(result.errors);
        if (!profile || !result.payload) return false;
        const submittedPayload = result.payload;
        const submittedFields = savedFields.filter((key) =>
            Object.prototype.hasOwnProperty.call(submittedPayload, key)
        );
        const submittedDraftSnapshot = Object.fromEntries(
            submittedFields.map((key) => [key, draft[key]])
        ) as Partial<ManagerProfile>;
        setWorking(true);
        try {
            const updated = await AIManagerService.updateProfile(profile.id, submittedPayload);
            const serverDraft = profileDraftFromServer(updated);
            setProfile(updated);
            setManagerProfiles((values) => values.map((manager) => (manager.id === updated.id ? updated : manager)));
            setDraft((currentDraft) => {
                const acknowledgedFields = submittedFields.filter((key) =>
                    profileValueEquals(currentDraft[key], submittedDraftSnapshot[key])
                );
                acknowledgedFields.forEach((key) => dirtyProfileFields.current.delete(key));
                const dirtyFields = Array.from(dirtyProfileFields.current).filter(
                    (key) => !profileValueEquals(currentDraft[key], serverDraft[key])
                );
                dirtyProfileFields.current = new Set(dirtyFields);
                canonicalProfileDraft.current = serverDraft;
                return mergeSavedProfileScope(serverDraft, currentDraft, acknowledgedFields, dirtyFields);
            });
            resetTest();
            try {
                setAccess(await AIManagerService.getAccess(profile.id));
            } catch {
                // The profile is saved; access readiness can be refreshed on the next workspace action.
            }
            setProfileSaveErrors({});
            enqueueSnackbar(successMessage, { variant: 'success' });
            return true;
        } catch (error) {
            const message = errorMessage(error, 'Не удалось сохранить изменения');
            setProfileSaveErrors({ form: message });
            enqueueSnackbar(message, { variant: 'error' });
            return false;
        } finally {
            setWorking(false);
        }
    };

    const createManager = async () => {
        if (working || !access?.can_create) return;
        if (!createIdempotencyKey.current) {
            createIdempotencyKey.current =
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `manager-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        setWorking(true);
        try {
            const created = await AIManagerService.createProfile(
                { name: 'Новый менеджер' },
                createIdempotencyKey.current
            );
            createIdempotencyKey.current = null;
            await openProfile(created.id);
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось создать AI-менеджера. Повторите ещё раз.'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const performSelection = (target: number | 'list') => {
        setPendingSelection(null);
        if (target === 'list') void loadList();
        else void openProfile(target);
    };

    const requestSelection = (target: number | 'list') => {
        if (hasUnsavedManagerChanges) setPendingSelection(target);
        else performSelection(target);
    };

    const updateDraftValues = (values: Partial<ManagerProfile>) => {
        Object.entries(values).forEach(([field, value]) => {
            const key = field as keyof ManagerProfile;
            if (profileValueEquals(value, canonicalProfileDraft.current[key])) dirtyProfileFields.current.delete(key);
            else dirtyProfileFields.current.add(key);
        });
        setDraft((current) => ({ ...current, ...values }));
        setProfileSaveErrors({});
    };

    const selectGoalTemplate = (template: (typeof goalTemplates)[number]) => {
        if (draft.goal_template === template.value) return;
        if (template.value === 'custom') {
            updateDraftValues({ goal_template: 'custom' });
            return;
        }
        updateDraftValues({
            goal_template: template.value,
            goal_description: template.goal,
            success_criteria: template.success,
            handoff_rule: template.handoff,
        });
    };

    const advanceWizard = async () => {
        if (wizardStep === wizardSteps.length - 1) {
            if (knowledgeBody.trim()) {
                setKnowledgeDraftError(true);
                return;
            }
            setSection('launch');
            return;
        }
        const steps: Array<{
            scope: ProfileSaveScope;
            result: ProfilePayloadResult;
            fields: Array<keyof ManagerProfile>;
            message: string;
        }> = [
            {
                scope: 'opening',
                result: buildOpeningPayload(draft),
                fields: openingScopeFields,
                message: 'Первое сообщение сохранено',
            },
            {
                scope: 'goal',
                result: buildGoalPayload(draft),
                fields: goalScopeFields,
                message: 'Цель сохранена',
            },
            {
                scope: 'company',
                result: buildCompanyPayload(draft),
                fields: companyScopeFields,
                message: 'Контекст компании сохранён',
            },
        ];
        const current = steps[wizardStep];
        const saved = await saveProfileScope(current.scope, current.result, current.fields, current.message);
        if (!saved) return;
        const nextStep = wizardStep + 1;
        setWizardStep(nextStep);
    };

    const beginKnowledgeEdit = (document: KnowledgeDocument) => {
        setEditingKnowledgeId(document.id);
        setEditingKnowledgeTitle(document.title);
        setEditingKnowledgeBody(document.body);
    };

    const saveKnowledgeEdit = async () => {
        if (!editingKnowledgeId || !editingKnowledgeTitle.trim() || !editingKnowledgeBody.trim()) return;
        setWorking(true);
        try {
            const updated = await AIManagerService.updateKnowledge(
                editingKnowledgeId,
                editingKnowledgeTitle.trim(),
                editingKnowledgeBody.trim()
            );
            setDocuments((values) => values.map((document) => (document.id === updated.id ? updated : document)));
            setEditingKnowledgeId(null);
            resetTest();
            await refreshGranted();
            enqueueSnackbar('Материал сохранён', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось сохранить материал'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const saveKnowledge = async () => {
        if (!profile || !knowledgeTitle.trim() || !knowledgeBody.trim()) return;
        const title = knowledgeTitle.trim();
        const body = knowledgeBody.trim();
        const matchesDraft = (document: KnowledgeDocument) => document.title === title && document.body === body;
        const completeSave = async (document: KnowledgeDocument, latestDocuments?: KnowledgeDocument[]) => {
            setDocuments((values) => {
                const source = latestDocuments || values;
                return source.some((item) => item.id === document.id) ? source : [document, ...source];
            });
            setKnowledgeTitle('Правила общения');
            setKnowledgeBody('');
            setKnowledgeDraftError(false);
            resetTest();
            try {
                setAccess(await AIManagerService.getAccess(profile.id));
            } catch {
                // The document is saved; readiness can be refreshed by the next workspace action.
            }
            enqueueSnackbar('Материал добавлен', { variant: 'success' });
        };
        setWorking(true);
        try {
            const latestDocuments = await AIManagerService.listKnowledge(profile.id);
            const existing = latestDocuments.find(matchesDraft);
            const saved = existing || (await AIManagerService.createKnowledge(profile.id, title, body));
            await completeSave(saved, latestDocuments);
        } catch (error) {
            try {
                const latestDocuments = await AIManagerService.listKnowledge(profile.id);
                const recovered = latestDocuments.find(matchesDraft);
                if (recovered) {
                    await completeSave(recovered, latestDocuments);
                    return;
                }
            } catch {
                // Keep the draft for an explicit retry when reconciliation is unavailable.
            }
            enqueueSnackbar(errorMessage(error, 'Не удалось добавить материал'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const removeKnowledge = async (documentId: number) => {
        setWorking(true);
        try {
            await AIManagerService.deleteKnowledge(documentId);
            setDocuments((values) => values.filter((document) => document.id !== documentId));
            resetTest();
            await refreshGranted();
            enqueueSnackbar('Материал удалён', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось удалить материал'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const saveAttachment = async () => {
        if (!profile || !attachmentTitle.trim() || (!attachmentUrl.trim() && !attachmentFile)) return;
        setWorking(true);
        try {
            const created = await AIManagerService.createAttachment(profile.id, {
                title: attachmentTitle.trim(),
                fileUrl: attachmentUrl.trim(),
                file: attachmentFile,
                caption: attachmentCaption.trim(),
            });
            setAttachments((values) => [created, ...values]);
            setAttachmentTitle('');
            setAttachmentUrl('');
            setAttachmentCaption('');
            setAttachmentFile(null);
            enqueueSnackbar('Вложение добавлено', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось добавить вложение'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const removeAttachment = async (attachmentId: number) => {
        setWorking(true);
        try {
            await AIManagerService.deleteAttachment(attachmentId);
            setAttachments((values) => values.filter((attachment) => attachment.id !== attachmentId));
            enqueueSnackbar('Вложение удалено', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось удалить вложение'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const sendTest = async () => {
        if (
            working ||
            !profile ||
            hasUnsavedManagerChanges ||
            !testInput.trim() ||
            testLimitsUnavailable ||
            testLimits?.session.remaining === 0 ||
            testLimits?.daily.remaining === 0
        )
            return;
        const message: TestMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: testInput.trim(),
        };
        const messages = [...testMessages, message];
        setTestMessages(messages);
        setTestInput('');
        setWorking(true);
        try {
            const result = await AIManagerService.testDialog(profile.id, messages, testSessionId, testSessionState);
            setTestMessages([...messages, result.assistantMessage]);
            setTestSessionId(result.sessionId);
            setTestSessionState(result.sessionState);
            setTestLimits(result.limits);
            setTestLimitsUnavailable(false);
        } catch (error) {
            setTestMessages(testMessages);
            setTestInput(message.content);
            const limits = testDialogLimitsFromError(error);
            if (limits) {
                setTestLimits(limits);
            } else if (profile) {
                try {
                    setTestLimits(await AIManagerService.getTestDialogLimits(profile.id, testSessionId));
                } catch {
                    // Preserve the original failure; a later refresh can restore the status.
                }
            }
            if (testDialogErrorCode(error) === 'test_dialog_limit_unavailable') {
                setTestLimitsUnavailable(true);
            }
            enqueueSnackbar(errorMessage(error, 'Не удалось получить тестовый ответ'), { variant: 'error' });
        } finally {
            setWorking(false);
        }
    };

    const acceptTest = async () => {
        if (!profile || !testSessionId || hasUnsavedManagerChanges) return;
        setWorking(true);
        try {
            await AIManagerService.acceptSetupTest(profile.id, testSessionId, testRating);
            await refreshGranted();
            enqueueSnackbar('Тест принят', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось принять тест'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const changeManagerStatus = async (nextStatus: 'running' | 'paused') => {
        if (!profile || (nextStatus === 'running' && hasUnsavedManagerChanges)) return;
        setWorking(true);
        try {
            await AIManagerService.updateStatus(profile.id, nextStatus);
            await refreshGranted();
            enqueueSnackbar(nextStatus === 'running' ? 'AI-менеджер запущен' : 'AI-менеджер на паузе', {
                variant: 'success',
            });
        } catch (error) {
            await refreshGranted();
            enqueueSnackbar(errorMessage(error, 'Не удалось изменить статус'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const openConversation = async (conversation: ManagerConversation) => {
        if (!profile) return;
        setWorking(true);
        try {
            const [result, conversationOutbounds] = await Promise.all([
                AIManagerService.getConversationTimeline(profile.id, conversation.id),
                AIManagerService.listOutbounds(profile.id, conversation.id),
            ]);
            setSelectedConversation(result.conversation);
            setTimeline(result.timeline);
            setOutbounds(conversationOutbounds);
            setOutboundDrafts(
                Object.fromEntries(conversationOutbounds.map((outbound) => [outbound.id, outbound.body]))
            );
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось открыть диалог'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const refreshConversation = async (conversationId: number) => {
        if (!profile) return;
        const [result, managerConversations, conversationOutbounds] = await Promise.all([
            AIManagerService.getConversationTimeline(profile.id, conversationId),
            AIManagerService.listConversations(profile.id),
            AIManagerService.listOutbounds(profile.id, conversationId),
        ]);
        setSelectedConversation(result.conversation);
        setTimeline(result.timeline);
        setConversations(managerConversations);
        setOutbounds(conversationOutbounds);
        setOutboundDrafts(Object.fromEntries(conversationOutbounds.map((outbound) => [outbound.id, outbound.body])));
    };

    const handoffConversation = async () => {
        if (!profile || !selectedConversation) return;
        setWorking(true);
        try {
            await AIManagerService.handoffConversation(profile.id, selectedConversation.id, handoffNote.trim());
            setHandoffNote('');
            await refreshConversation(selectedConversation.id);
            enqueueSnackbar('Диалог передан сотруднику', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось передать диалог'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const releaseConversation = async () => {
        if (!profile || !selectedConversation) return;
        setWorking(true);
        try {
            await AIManagerService.setOperatorOverride(profile.id, selectedConversation.id, false);
            await refreshConversation(selectedConversation.id);
            enqueueSnackbar('Диалог возвращён AI-менеджеру', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось вернуть диалог'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const moveConversation = async (salesStage: string) => {
        if (!profile || !selectedConversation) return;
        setWorking(true);
        try {
            await AIManagerService.moveConversationStage(profile.id, selectedConversation.id, salesStage);
            await refreshConversation(selectedConversation.id);
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось изменить этап'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const finishConversation = async (toStatus: 'goal_completed' | 'disqualified' | 'stopped' | 'failed') => {
        if (!profile || !selectedConversation) return;
        setWorking(true);
        try {
            await AIManagerService.resolveConversation(profile.id, selectedConversation.id, toStatus);
            await refreshConversation(selectedConversation.id);
            enqueueSnackbar('Диалог завершён', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось завершить диалог'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const approveOutbound = async (outbound: ManagerOutbound) => {
        setWorking(true);
        try {
            await AIManagerService.approveOutbound(outbound.id, outboundDrafts[outbound.id] || outbound.body);
            if (selectedConversation) await refreshConversation(selectedConversation.id);
            enqueueSnackbar('Сообщение подтверждено', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(errorMessage(error, 'Не удалось подтвердить сообщение'), {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const linkExistingTelegram = async () => {
        if (!profile || !selectedTelegramClientId) return;
        setWorking(true);
        setTelegramAuthNotice('');
        try {
            const updated = await AIManagerService.updateProfile(profile.id, {
                telegram_client: selectedTelegramClientId,
            });
            setProfile(updated);
            setManagerProfiles((values) => values.map((manager) => (manager.id === updated.id ? updated : manager)));
            resetTest();
            await refreshGranted();
            enqueueSnackbar('Telegram выбран для этого менеджера', {
                variant: 'success',
            });
        } catch (error) {
            const message = errorMessage(error, 'Не удалось подключить выбранный Telegram');
            setTelegramAuthNotice(message);
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setWorking(false);
        }
    };

    const resetTelegramAuth = (clearPhone = false, notice = '') => {
        setTelegramToken('');
        setTelegramCode('');
        setTelegramPassword('');
        setTelegramStage('phone');
        setTelegramAuthNotice(notice);
        if (clearPhone) setPhone('');
    };

    const startTelegram = async () => {
        if (!profile || !phone.trim()) return;
        setWorking(true);
        setTelegramCode('');
        setTelegramPassword('');
        setTelegramAuthNotice('');
        try {
            const session = await AIManagerService.startTelegramAuth(profile.id, phone.trim());
            setTelegramToken(session.session_token);
            setTelegramStage(session.status === 'password_required' ? 'password' : 'code');
            enqueueSnackbar('Код отправлен в Telegram', { variant: 'success' });
        } catch (error) {
            const message = errorMessage(error, 'Не удалось отправить код');
            setTelegramAuthNotice(message);
            enqueueSnackbar(message, {
                variant: 'error',
            });
        } finally {
            setWorking(false);
        }
    };

    const verifyTelegram = async () => {
        if (!telegramToken) return;
        setWorking(true);
        try {
            const session = await AIManagerService.verifyTelegramAuth(
                telegramToken,
                telegramStage === 'code' ? telegramCode.trim() : '',
                telegramStage === 'password' ? telegramPassword : ''
            );
            if (session.status === 'completed') {
                resetTelegramAuth();
                await refreshGranted();
                enqueueSnackbar('Telegram подключён', { variant: 'success' });
            }
        } catch (error) {
            const code = telegramAuthErrorCode(error);
            const message = errorMessage(error, 'Не удалось подтвердить Telegram');
            if (code === 'password_required') {
                setTelegramCode('');
                setTelegramPassword('');
                setTelegramStage('password');
                setTelegramAuthNotice('');
                enqueueSnackbar(message, { variant: 'info' });
            } else if (code === 'invalid_password') {
                setTelegramPassword('');
                enqueueSnackbar(message, { variant: 'error' });
            } else if (code === 'invalid_code') {
                setTelegramCode('');
                enqueueSnackbar(message, { variant: 'error' });
            } else if (terminalTelegramAuthErrors.has(code)) {
                resetTelegramAuth(false, message);
                enqueueSnackbar(message, { variant: 'error' });
            } else {
                enqueueSnackbar(message, { variant: 'error' });
            }
        } finally {
            setWorking(false);
        }
    };

    const updateDraft = (key: keyof ManagerProfile, value: unknown) => {
        if (profileValueEquals(value, canonicalProfileDraft.current[key])) {
            dirtyProfileFields.current.delete(key);
        } else {
            dirtyProfileFields.current.add(key);
        }
        setDraft((current) => ({ ...current, [key]: value }));
        setProfileSaveErrors((current) => {
            if (!current[key] && !current.form) return current;
            const next = { ...current };
            delete next[key];
            delete next.form;
            return next;
        });
    };

    const locked = () => {
        const pending = access?.state === 'pending';
        const rejected = access?.state === 'rejected';
        return (
            <Paper sx={{ ...cardSx, p: { xs: 2.5, md: 4 } }}>
                <Stack
                    spacing={1.5}
                    alignItems="flex-start"
                >
                    <Typography variant="h2">
                        {pending ? 'Заявка на проверке' : rejected ? 'Доступ не открыт' : 'AI-менеджер пока закрыт'}
                    </Typography>
                    <Typography color="secondary.main">
                        {pending || rejected
                            ? access?.next_step
                            : 'Оставьте заявку. Доступ откроется после проверки заявки.'}
                    </Typography>
                    {!pending && !rejected && (
                        <Button
                            variant="contained"
                            onClick={requestAccess}
                            disabled={working}
                        >
                            Запросить доступ
                        </Button>
                    )}
                </Stack>
            </Paper>
        );
    };

    const overview = () => (
        <Stack spacing={2}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
            >
                {[
                    ['Статус', profile?.status_label || 'Черновик'],
                    ['Диалоги', String(access?.workspace?.launch.credits_available ?? 0)],
                    ['Telegram', telegramLabel(profile?.telegram_status, access?.workspace?.launch.transport.code)],
                ].map(([label, value]) => (
                    <Paper
                        key={label}
                        sx={{ ...cardSx, p: 2.5, flex: 1 }}
                    >
                        <Typography
                            variant="body2"
                            color="secondary.main"
                        >
                            {label}
                        </Typography>
                        <Typography
                            variant="h3"
                            mt={0.5}
                        >
                            {value}
                        </Typography>
                    </Paper>
                ))}
            </Stack>
            {access?.workspace?.launch.blockers.length ? (
                <Alert severity="warning">
                    <Typography fontWeight={700}>Что нужно для запуска</Typography>
                    {access.workspace.launch.blockers.map((code) => (
                        <Typography
                            key={code}
                            variant="body2"
                        >
                            {blockerLabel(code)}
                        </Typography>
                    ))}
                </Alert>
            ) : (
                <Alert severity="success">Менеджер готов к запуску с вашего Telegram-аккаунта.</Alert>
            )}
            {access?.workspace?.launch.can_continue_charged_dialogs &&
                !access.workspace.launch.can_start_new_dialog && (
                    <Alert severity="info">
                        Новые диалоги приостановлены: остаток исчерпан. Уже оплаченные диалоги продолжат работу.
                    </Alert>
                )}
            {hasUnsavedManagerChanges && (
                <Alert severity="warning">
                    Сохраните изменения в настройках перед запуском. Менеджер использует последнюю сохранённую версию.
                </Alert>
            )}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
            >
                {profile?.status === 'running' ? (
                    <Button
                        variant="outlined"
                        onClick={() => void changeManagerStatus('paused')}
                        disabled={working}
                    >
                        Поставить на паузу
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={() => void changeManagerStatus('running')}
                        disabled={working || hasUnsavedManagerChanges || !access?.workspace?.launch.can_start}
                    >
                        Запустить менеджера
                    </Button>
                )}
                {!access?.workspace?.launch.transport.ready && (
                    <Typography
                        variant="body2"
                        color="secondary.main"
                    >
                        Отправка сообщений временно недоступна.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );

    const openingStep = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3">Как менеджер начнёт диалог</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        Напишите готовое первое сообщение. Оно задаст тон диалогу.
                    </Typography>
                </Box>
                {profileSaveErrorScope === 'opening' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                <TextField
                    label="Первое сообщение"
                    value={String(draft.opening_message ?? '')}
                    onChange={(event) => updateDraft('opening_message', event.target.value)}
                    error={Boolean(profileSaveErrors.opening_message)}
                    helperText={
                        profileSaveErrors.opening_message ||
                        'До 1000 символов. Например: «Добрый день! Подсказать подробнее?»'
                    }
                    multiline
                    minRows={4}
                    maxRows={6}
                    sx={fieldSx}
                />
                <TextField
                    label="Имя AI-менеджера"
                    value={String(draft.name ?? '')}
                    onChange={(event) => updateDraft('name', event.target.value)}
                    error={Boolean(profileSaveErrors.name)}
                    helperText={profileSaveErrors.name || 'До 255 символов. Видно только вам в списке менеджеров.'}
                    sx={fieldSx}
                />
            </Stack>
        </Paper>
    );

    const goalStep = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3">Что менеджер должен сделать</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        Выберите близкую цель и поправьте результат под свою задачу.
                    </Typography>
                </Box>
                {profileSaveErrorScope === 'goal' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1,
                    }}
                >
                    {goalTemplates.map((template) => {
                        const selected = (draft.goal_template || 'custom') === template.value;
                        return (
                            <Button
                                key={template.value}
                                variant={selected ? 'contained' : 'outlined'}
                                onClick={() => selectGoalTemplate(template)}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    minHeight: 48,
                                }}
                            >
                                {template.label}
                            </Button>
                        );
                    })}
                </Box>
                <TextField
                    label="Ожидаемый результат"
                    value={String(draft.goal_description ?? '')}
                    onChange={(event) => updateDraft('goal_description', event.target.value)}
                    multiline
                    minRows={3}
                    maxRows={6}
                    error={Boolean(profileSaveErrors.goal_description)}
                    helperText={
                        profileSaveErrors.goal_description || 'Конкретно опишите, чем должен закончиться диалог.'
                    }
                    sx={fieldSx}
                />
            </Stack>
        </Paper>
    );

    const companyStep = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3">Что менеджеру знать о вас</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        Кратко опишите компанию, продукт, клиентов и важные ограничения.
                    </Typography>
                </Box>
                {profileSaveErrorScope === 'company' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                <TextField
                    label="О компании и продукте"
                    value={String(draft.description ?? '')}
                    onChange={(event) => updateDraft('description', event.target.value)}
                    multiline
                    minRows={6}
                    maxRows={8}
                    error={Boolean(profileSaveErrors.description)}
                    helperText={
                        profileSaveErrors.description || 'Что продаёте, для кого, цены, география и ограничения.'
                    }
                    sx={fieldSx}
                />
            </Stack>
        </Paper>
    );

    const sourceSettings = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Typography variant="h3">Источники лидов</Typography>
                <TextField
                    select
                    label="Источник"
                    value={sourceMode}
                    onChange={(event) => updateDraft('source_mode', event.target.value as ManagerIntakeSourceMode)}
                    sx={fieldSx}
                >
                    <MenuItem value="packages">Пакетные потоки</MenuItem>
                    <MenuItem value="subscription">Подписки TGHunter</MenuItem>
                    <MenuItem value="both">Потоки и подписки</MenuItem>
                </TextField>
                {(sourceMode === 'packages' || sourceMode === 'both') && (
                    <>
                        <TextField
                            select
                            label="Потоки лидов"
                            value={selectedLeadStreamIds}
                            onChange={(event) => updateDraft('lead_stream_ids', numericSelection(event.target.value))}
                            disabled={!intakeOptions?.package_streams.length}
                            error={Boolean(profileSaveErrors.lead_stream_ids)}
                            helperText={profileSaveErrors.lead_stream_ids}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    const ids = numericSelection(selected);
                                    const labels = (intakeOptions?.package_streams || [])
                                        .filter((stream) => ids.includes(stream.id))
                                        .map((stream) => stream.display_name || stream.name);
                                    return labels.join(', ') || 'Не выбраны';
                                },
                            }}
                            sx={fieldSx}
                        >
                            {(intakeOptions?.package_streams || []).map((stream) => (
                                <MenuItem
                                    key={stream.id}
                                    value={stream.id}
                                >
                                    {stream.display_name || stream.name} · {stream.status_label} · лидов{' '}
                                    {stream.balance}
                                </MenuItem>
                            ))}
                        </TextField>
                        {!intakeOptions?.package_streams.length && (
                            <Alert severity="warning">Доступных пакетных потоков нет.</Alert>
                        )}
                    </>
                )}
                {(sourceMode === 'subscription' || sourceMode === 'both') && (
                    <>
                        <TextField
                            select
                            label="Проекты подписки"
                            value={selectedSubscriptionProjectIds}
                            onChange={(event) =>
                                updateDraft('subscription_project_ids', numericSelection(event.target.value))
                            }
                            disabled={!intakeOptions?.subscription_projects.length}
                            error={Boolean(profileSaveErrors.subscription_project_ids)}
                            helperText={profileSaveErrors.subscription_project_ids}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    const ids = numericSelection(selected);
                                    const labels = (intakeOptions?.subscription_projects || [])
                                        .filter((project) => ids.includes(project.id))
                                        .map((project) => project.name);
                                    return labels.join(', ') || 'Не выбраны';
                                },
                            }}
                            sx={fieldSx}
                        >
                            {(intakeOptions?.subscription_projects || []).map((project) => (
                                <MenuItem
                                    key={project.id}
                                    value={project.id}
                                    disabled={
                                        (!project.is_active || !project.subscription_active) &&
                                        !selectedSubscriptionProjectIds.includes(project.id)
                                    }
                                >
                                    {project.name}
                                    {!project.subscription_active ? ' · подписка неактивна' : ''}
                                    {!project.ai_analysis_enabled ? ' · AI-анализ выключен' : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        {!intakeOptions?.subscription_projects.length && (
                            <Alert severity="warning">Доступных проектов подписки нет.</Alert>
                        )}
                    </>
                )}
                <TextField
                    select
                    label="Какие сообщения брать"
                    value={leadSelection}
                    onChange={(event) => updateDraft('lead_selection', event.target.value as ManagerLeadSelection)}
                    sx={fieldSx}
                >
                    <MenuItem value="target_only">Только целевые</MenuItem>
                    <MenuItem value="all">Все сообщения выбранных источников</MenuItem>
                </TextField>
                {leadSelection === 'all' && (
                    <Alert severity="info">
                        Только выбранные ваши источники: без спама, с известным автором и свежими сообщениями. Общая
                        лента не используется.
                    </Alert>
                )}
                {showSubscriptionAiWarning && (
                    <Alert severity="warning">
                        В выбранных проектах AI-анализ выключен. Менеджер возьмёт только сообщения, явно отмеченные как
                        целевые. Сообщения без такой отметки не попадут в работу.
                    </Alert>
                )}
                <Typography
                    variant="body2"
                    color="secondary.main"
                >
                    Один AI-диалог списывается один раз, когда менеджер впервые отвечает по лиду. Лимит сообщений в
                    диалоге настраивается в дополнительных настройках. Баланс лидов потока считается отдельно.
                </Typography>
                {profileSaveErrorScope === 'sources' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                <Box>
                    <Button
                        variant="contained"
                        onClick={() =>
                            void saveProfileScope(
                                'sources',
                                buildSourcesPayload(
                                    sourceMode,
                                    leadSelection,
                                    selectedLeadStreamIds,
                                    selectedSubscriptionProjectIds
                                ),
                                sourceScopeFields,
                                'Источники сохранены'
                            )
                        }
                        disabled={working}
                    >
                        Сохранить источники
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );

    const deliveryMode = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3">Как отправлять сообщения</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        В безопасном режиме вы проверяете черновики. Авторежим отправляет их без ручной проверки.
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                >
                    <Button
                        variant={!draft.auto_send_private_default ? 'contained' : 'outlined'}
                        onClick={() => updateDraft('auto_send_private_default', false)}
                    >
                        Проверять черновики человеком
                    </Button>
                    <Button
                        variant={draft.auto_send_private_default ? 'contained' : 'outlined'}
                        onClick={() => updateDraft('auto_send_private_default', true)}
                    >
                        Отправлять автоматически
                    </Button>
                </Stack>
                <Box>
                    <Button
                        variant="contained"
                        onClick={() =>
                            void saveProfileScope(
                                'schedule',
                                {
                                    payload: {
                                        auto_send_private_default: Boolean(draft.auto_send_private_default),
                                    },
                                    errors: {},
                                },
                                ['auto_send_private_default'],
                                'Режим отправки сохранён'
                            )
                        }
                        disabled={working}
                    >
                        Сохранить режим
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );

    const limitsSettings = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Typography variant="h3">Расписание и лимиты</Typography>
                {profileSaveErrorScope === 'schedule' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                <Autocomplete
                    freeSolo
                    clearText="Очистить"
                    openText="Открыть"
                    closeText="Закрыть"
                    options={timezoneOptions}
                    value={String(draft.working_timezone ?? '')}
                    onChange={(_event, value) => updateDraft('working_timezone', value || '')}
                    onInputChange={(_event, value) => updateDraft('working_timezone', value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Часовой пояс"
                            placeholder="Europe/Moscow"
                            error={Boolean(profileSaveErrors.working_timezone)}
                            helperText={profileSaveErrors.working_timezone || 'Например, Europe/Moscow'}
                            sx={fieldSx}
                        />
                    )}
                />
                <Box>
                    <Typography
                        variant="body2"
                        fontWeight={700}
                        mb={1}
                    >
                        Рабочие дни
                    </Typography>
                    <ToggleButtonGroup
                        value={draft.working_days || []}
                        onChange={(_event, days: string[]) => updateDraft('working_days', days)}
                        aria-label="Рабочие дни"
                        size="small"
                        color="primary"
                    >
                        {workingDayOptions.map(([value, label]) => (
                            <ToggleButton
                                key={value}
                                value={value}
                                aria-label={label}
                            >
                                {label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: 1.5,
                    }}
                >
                    <TextField
                        fullWidth
                        label="Начало работы"
                        type="time"
                        value={String(draft.working_hours_start || '')}
                        onChange={(event) => updateDraft('working_hours_start', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(profileSaveErrors.working_hours_start)}
                        helperText={profileSaveErrors.working_hours_start}
                        sx={fieldSx}
                    />
                    <TextField
                        fullWidth
                        label="Окончание работы"
                        type="time"
                        value={String(draft.working_hours_end || '')}
                        onChange={(event) => updateDraft('working_hours_end', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(profileSaveErrors.working_hours_end)}
                        helperText={profileSaveErrors.working_hours_end}
                        sx={fieldSx}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: 1.5,
                    }}
                >
                    {(
                        [
                            ['reply_delay_min_seconds', 'Мин. задержка, сек', 0, 2147483647],
                            ['reply_delay_max_seconds', 'Макс. задержка, сек', 0, 2147483647],
                            ['max_new_conversations_per_day', 'Новых диалогов в день', 30, 50],
                            ['max_outbound_messages_per_hour', 'Сообщений в час', 1, 60],
                            ['max_messages_per_dialog', 'Сообщений в диалоге', 1, 300],
                        ] as Array<[keyof ManagerProfile, string, number, number]>
                    ).map(([key, label, minimum, maximum]) => (
                        <TextField
                            key={key}
                            fullWidth
                            type="number"
                            label={label}
                            value={draft[key] ?? ''}
                            onChange={(event) =>
                                updateDraft(key, event.target.value === '' ? '' : Number(event.target.value))
                            }
                            inputProps={{ min: minimum, max: maximum, step: 1 }}
                            error={Boolean(profileSaveErrors[key])}
                            helperText={profileSaveErrors[key]}
                            sx={fieldSx}
                        />
                    ))}
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={Boolean(draft.no_reply_followup_enabled)}
                            onChange={(event) => updateDraft('no_reply_followup_enabled', event.target.checked)}
                        />
                    }
                    label="Напоминать, если клиент не ответил"
                />
                {draft.no_reply_followup_enabled && (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                    >
                        <TextField
                            label="Через, часов"
                            type="number"
                            value={draft.no_reply_followup_delay_hours ?? ''}
                            onChange={(event) =>
                                updateDraft(
                                    'no_reply_followup_delay_hours',
                                    event.target.value === '' ? '' : Number(event.target.value)
                                )
                            }
                            inputProps={{ min: 1, max: 168, step: 1 }}
                            error={Boolean(profileSaveErrors.no_reply_followup_delay_hours)}
                            helperText={profileSaveErrors.no_reply_followup_delay_hours}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Текст напоминания"
                            value={String(draft.no_reply_followup_text ?? '')}
                            onChange={(event) => updateDraft('no_reply_followup_text', event.target.value)}
                            error={Boolean(profileSaveErrors.no_reply_followup_text)}
                            helperText={profileSaveErrors.no_reply_followup_text}
                            sx={fieldSx}
                        />
                    </Stack>
                )}
                <Box>
                    <Button
                        variant="contained"
                        onClick={() =>
                            void saveProfileScope(
                                'schedule',
                                buildSchedulePayload(draft),
                                scheduleScopeFields,
                                'Расписание сохранено'
                            )
                        }
                        disabled={working}
                    >
                        Сохранить расписание
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );

    const advancedScenario = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Typography variant="h3">Тонкая настройка сценария</Typography>
                {profileSaveErrorScope === 'scenario' && profileSaveErrors.form && (
                    <Alert severity="error">{profileSaveErrors.form}</Alert>
                )}
                {(
                    [
                        [
                            'handoff_rule',
                            'Когда передать сотруднику',
                            2,
                            'Например: клиент готов обсудить договор или просит специалиста',
                            'Условие передачи диалога человеку.',
                        ],
                        [
                            'success_criteria',
                            'Критерий успеха',
                            2,
                            'Например: согласованы дата и время созвона',
                            'По какому результату считать цель достигнутой.',
                        ],
                        ['rejection_criteria', 'Когда остановиться', 2, '', 'Когда менеджер должен завершить диалог.'],
                    ] as Array<[keyof ManagerProfile, string, number, string, string]>
                ).map(([key, label, rows, placeholder, helperText]) => (
                    <TextField
                        key={key}
                        label={label}
                        value={String(draft[key] ?? '')}
                        onChange={(event) => updateDraft(key, event.target.value)}
                        multiline
                        minRows={rows}
                        maxRows={6}
                        placeholder={placeholder || undefined}
                        error={Boolean(profileSaveErrors[key])}
                        helperText={profileSaveErrors[key] || helperText}
                        sx={fieldSx}
                    />
                ))}
                <TextField
                    label="Сценарий"
                    helperText={profileSaveErrors.flow_steps || 'Один шаг на строку'}
                    value={(draft.flow_steps || []).join('\n')}
                    onChange={(event) => updateDraft('flow_steps', event.target.value.split('\n'))}
                    multiline
                    minRows={5}
                    maxRows={8}
                    error={Boolean(profileSaveErrors.flow_steps)}
                    sx={fieldSx}
                />
                <TextField
                    label="Что нужно узнать"
                    helperText={profileSaveErrors.data_to_collect || 'Один пункт на строку'}
                    value={(draft.data_to_collect || []).join('\n')}
                    onChange={(event) => updateDraft('data_to_collect', event.target.value.split('\n'))}
                    multiline
                    minRows={3}
                    maxRows={6}
                    error={Boolean(profileSaveErrors.data_to_collect)}
                    sx={fieldSx}
                />
                <TextField
                    label="Стоп-фразы"
                    helperText={profileSaveErrors.stop_phrases || 'Одна фраза на строку'}
                    value={(draft.stop_phrases || []).join('\n')}
                    onChange={(event) => updateDraft('stop_phrases', event.target.value.split('\n'))}
                    multiline
                    minRows={3}
                    maxRows={6}
                    error={Boolean(profileSaveErrors.stop_phrases)}
                    sx={fieldSx}
                />
                <Box>
                    <Button
                        variant="contained"
                        onClick={() =>
                            void saveProfileScope(
                                'scenario',
                                buildAdvancedScenarioPayload(draft),
                                advancedScenarioScopeFields,
                                'Дополнительные настройки сохранены'
                            )
                        }
                        disabled={working}
                    >
                        Сохранить настройки
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );

    const knowledge = () => (
        <Stack spacing={2}>
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h3">Правила общения</Typography>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                            mt={0.5}
                        >
                            Необязательно. Напишите, как отвечать клиентам: что уточнять, что предлагать и чего не обещать.
                        </Typography>
                    </Box>
                    <TextField
                        label="Правила общения"
                        value={knowledgeBody}
                        onChange={(event) => {
                            setKnowledgeBody(event.target.value);
                            setKnowledgeDraftError(false);
                        }}
                        multiline
                        minRows={5}
                        maxRows={8}
                        sx={fieldSx}
                    />
                    {knowledgeDraftError && (
                        <Alert severity="warning">Добавьте материал или очистите поле перед переходом.</Alert>
                    )}
                    <TextField
                        label="Название материала"
                        value={knowledgeTitle}
                        onChange={(event) => setKnowledgeTitle(event.target.value)}
                        sx={fieldSx}
                    />
                    <Box>
                        <Button
                            variant="contained"
                            onClick={saveKnowledge}
                            disabled={working || !knowledgeTitle.trim() || !knowledgeBody.trim()}
                        >
                            Добавить материал
                        </Button>
                    </Box>
                </Stack>
            </Paper>
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Typography
                    variant="h3"
                    mb={2}
                >
                    База знаний
                </Typography>
                {documents.length === 0 ? (
                    <Typography color="secondary.main">Материалов пока нет.</Typography>
                ) : (
                    <Stack
                        divider={<Divider flexItem />}
                        spacing={2}
                    >
                        {documents.map((document) => (
                            <Box key={document.id}>
                                {editingKnowledgeId === document.id ? (
                                    <Stack spacing={1.5}>
                                        <TextField
                                            label="Название"
                                            value={editingKnowledgeTitle}
                                            onChange={(event) => setEditingKnowledgeTitle(event.target.value)}
                                            sx={fieldSx}
                                        />
                                        <TextField
                                            label="Материал"
                                            value={editingKnowledgeBody}
                                            onChange={(event) => setEditingKnowledgeBody(event.target.value)}
                                            multiline
                                            minRows={6}
                                            maxRows={8}
                                            sx={fieldSx}
                                        />
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                        >
                                            <Button
                                                variant="contained"
                                                onClick={saveKnowledgeEdit}
                                                disabled={
                                                    working ||
                                                    !editingKnowledgeTitle.trim() ||
                                                    !editingKnowledgeBody.trim()
                                                }
                                            >
                                                Сохранить
                                            </Button>
                                            <Button
                                                onClick={() => setEditingKnowledgeId(null)}
                                                disabled={working}
                                            >
                                                Отмена
                                            </Button>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack
                                        spacing={1}
                                        alignItems="flex-start"
                                    >
                                        <Typography fontWeight={700}>{document.title}</Typography>
                                        <Typography
                                            variant="body2"
                                            color="secondary.main"
                                            sx={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {document.body}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => beginKnowledgeEdit(document)}
                                        >
                                            Изменить
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => void removeKnowledge(document.id)}
                                            disabled={working}
                                        >
                                            Удалить
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}
            </Paper>
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Stack spacing={2}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        spacing={1.5}
                    >
                        <Box>
                            <Typography variant="h3">Вложения</Typography>
                            <Typography
                                variant="body2"
                                color="secondary.main"
                                mt={0.5}
                            >
                                Необязательные файлы для ручной отправки клиенту.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={() => setShowAttachments((value) => !value)}
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                        >
                            {showAttachments
                                ? 'Скрыть вложения'
                                : attachments.length
                                ? 'Вложения (' + attachments.length + ')'
                                : 'Прикрепить файл'}
                        </Button>
                    </Stack>
                    {showAttachments && (
                        <Stack spacing={2}>
                            <TextField
                                label="Название"
                                value={attachmentTitle}
                                onChange={(event) => setAttachmentTitle(event.target.value)}
                                sx={fieldSx}
                            />
                            <TextField
                                label="Ссылка на файл"
                                value={attachmentUrl}
                                onChange={(event) => setAttachmentUrl(event.target.value)}
                                helperText="Можно указать ссылку или загрузить файл ниже"
                                sx={fieldSx}
                            />
                            <Button
                                component="label"
                                variant="outlined"
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                {attachmentFile ? attachmentFile.name : 'Выбрать файл'}
                                <input
                                    hidden
                                    type="file"
                                    onChange={(event) => setAttachmentFile(event.target.files?.[0] || null)}
                                />
                            </Button>
                            <TextField
                                label="Подпись"
                                value={attachmentCaption}
                                onChange={(event) => setAttachmentCaption(event.target.value)}
                                sx={fieldSx}
                            />
                            <Box>
                                <Button
                                    variant="contained"
                                    onClick={() => void saveAttachment()}
                                    disabled={
                                        working || !attachmentTitle.trim() || (!attachmentUrl.trim() && !attachmentFile)
                                    }
                                >
                                    Добавить вложение
                                </Button>
                            </Box>
                            {attachments.length === 0 ? (
                                <Typography color="secondary.main">Вложений пока нет.</Typography>
                            ) : (
                                <Stack
                                    divider={<Divider flexItem />}
                                    spacing={1.5}
                                >
                                    {attachments.map((attachment) => (
                                        <Stack
                                            key={attachment.id}
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        >
                                            <Box>
                                                <Typography fontWeight={700}>{attachment.title}</Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="secondary.main"
                                                >
                                                    {attachment.caption ||
                                                        attachment.file_url ||
                                                        attachment.uploaded_file_url}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => void removeAttachment(attachment.id)}
                                                disabled={working}
                                            >
                                                Удалить
                                            </Button>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Stack>
    );

    const test = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                    <Box>
                        <Typography variant="h3">Тестовый диалог</Typography>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                            mt={0.5}
                        >
                            Сообщения никуда не отправляются.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={resetTest}
                        disabled={working || (testMessages.length === 0 && !testInput)}
                    >
                        Сбросить диалог
                    </Button>
                </Stack>
                {hasUnsavedManagerChanges && (
                    <Alert severity="warning">
                        Сохраните изменения в настройках менеджера. Тест использует последнюю сохранённую версию.
                    </Alert>
                )}
                {testLimitsUnavailable ? (
                    <Alert severity="warning">
                        Лимиты сейчас недоступны. Отправка тестовых сообщений временно приостановлена.
                    </Alert>
                ) : testLimits ? (
                    <Box>
                        <Typography variant="body2">
                            Осталось ответов: {testLimits.session.remaining} в этом диалоге,{' '}
                            {testLimits.daily.remaining} сегодня.
                        </Typography>
                        <Typography
                            variant="caption"
                            color="secondary.main"
                        >
                            Дневной лимит обновляется в 00:00 МСК. Попытки без ответа тоже учитываются.
                        </Typography>
                    </Box>
                ) : null}
                <Box
                    sx={{
                        minHeight: 220,
                        bgcolor: 'var(--lm-canvas)',
                        borderRadius: '8px',
                        p: 2,
                    }}
                >
                    {testMessages.length === 0 ? (
                        <Typography color="secondary.main">Напишите как потенциальный клиент.</Typography>
                    ) : (
                        <Stack spacing={1.25}>
                            {testMessages.map((message) => (
                                <Box
                                    key={message.id}
                                    alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                                    sx={{
                                        maxWidth: '82%',
                                        bgcolor:
                                            message.role === 'user' ? 'var(--lm-accent-soft)' : 'var(--lm-surface)',
                                        border: '1px solid var(--lm-border)',
                                        borderRadius: '8px',
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Typography variant="body2">{message.content}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                >
                    <TextField
                        fullWidth
                        label="Сообщение клиента"
                        value={testInput}
                        onChange={(event) => setTestInput(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                void sendTest();
                            }
                        }}
                        sx={fieldSx}
                    />
                    <Button
                        variant="contained"
                        onClick={sendTest}
                        disabled={
                            working ||
                            hasUnsavedManagerChanges ||
                            testLimitsUnavailable ||
                            !testInput.trim() ||
                            testLimits?.session.remaining === 0 ||
                            testLimits?.daily.remaining === 0
                        }
                    >
                        Отправить
                    </Button>
                </Stack>
                {testSessionId && testMessages.some((message) => message.role === 'assistant') && (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                        <TextField
                            select
                            label="Оценка"
                            value={testRating}
                            onChange={(event) => setTestRating(Number(event.target.value))}
                            sx={{ ...fieldSx, minWidth: 120 }}
                        >
                            {[8, 9, 10].map((value) => (
                                <MenuItem
                                    key={value}
                                    value={value}
                                >
                                    {value} из 10
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button
                            variant="outlined"
                            onClick={() => void acceptTest()}
                            disabled={working || hasUnsavedManagerChanges}
                        >
                            Принять тест
                        </Button>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                        >
                            После изменения сценария или базы знаний тест нужно принять заново.
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );

    const dialogs = () => (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="stretch"
        >
            <Paper sx={{ ...cardSx, p: 2, width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
                <Typography variant="h3">Диалоги</Typography>
                <Typography
                    variant="body2"
                    color="secondary.main"
                    mt={0.5}
                    mb={2}
                >
                    Очередь проверки и активные обращения.
                </Typography>
                {conversations.length === 0 ? (
                    <Typography color="secondary.main">Диалогов пока нет.</Typography>
                ) : (
                    <Stack
                        divider={<Divider flexItem />}
                        spacing={0}
                    >
                        {conversations.map((conversation) => (
                            <Button
                                key={conversation.id}
                                onClick={() => void openConversation(conversation)}
                                color="inherit"
                                sx={{
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                    textAlign: 'left',
                                    width: '100%',
                                    height: 'auto',
                                    minHeight: 88,
                                    py: 1.5,
                                    px: 1,
                                }}
                            >
                                <Box minWidth={0}>
                                    <Typography
                                        fontWeight={700}
                                        noWrap
                                    >
                                        {conversation.source_author_snapshot || `Диалог ${conversation.id}`}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="secondary.main"
                                        noWrap
                                    >
                                        {conversation.latest_message_preview || conversation.source_message_snapshot}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="secondary.main"
                                    >
                                        {conversation.sales_stage_title || conversation.status}
                                    </Typography>
                                </Box>
                            </Button>
                        ))}
                    </Stack>
                )}
            </Paper>
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 }, flex: 1, minWidth: 0 }}>
                {!selectedConversation ? (
                    <Typography color="secondary.main">Выберите диалог слева.</Typography>
                ) : (
                    <Stack spacing={2}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                            <Box>
                                <Typography variant="h3">
                                    {selectedConversation.source_author_snapshot || `Диалог ${selectedConversation.id}`}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="secondary.main"
                                >
                                    {selectedConversation.source_chat_title_snapshot || selectedConversation.status}
                                </Typography>
                            </Box>
                            <TextField
                                select
                                size="small"
                                label="Этап"
                                value={selectedConversation.sales_stage || 'new_contact'}
                                onChange={(event) => void moveConversation(event.target.value)}
                                disabled={working}
                                sx={{ ...fieldSx, minWidth: 190 }}
                            >
                                {[
                                    ['new_contact', 'Новый контакт'],
                                    ['interest_present', 'Есть интерес'],
                                    ['target_lead', 'Целевой лид'],
                                    ['decision_making', 'Принятие решения'],
                                    ['payment', 'Оплата'],
                                    ['closed', 'Закрыто'],
                                    ['rejected', 'Отказ'],
                                ].map(([value, label]) => (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                    >
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                        <Box
                            sx={{
                                bgcolor: 'var(--lm-canvas)',
                                borderRadius: '8px',
                                p: 2,
                                maxHeight: 420,
                                overflowY: 'auto',
                            }}
                        >
                            <Stack spacing={1.25}>
                                {timeline.map((item) => (
                                    <Box
                                        key={item.id}
                                        alignSelf={item.role === 'user' ? 'flex-start' : 'flex-end'}
                                        sx={{
                                            maxWidth: '86%',
                                            bgcolor:
                                                item.role === 'user' ? 'var(--lm-surface)' : 'var(--lm-accent-soft)',
                                            border: '1px solid var(--lm-border)',
                                            borderRadius: '8px',
                                            px: 1.5,
                                            py: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {item.content}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                        {outbounds
                            .filter((outbound) => outbound.status === 'draft')
                            .map((outbound) => (
                                <Stack
                                    key={outbound.id}
                                    spacing={1}
                                >
                                    <TextField
                                        label="Черновик ответа"
                                        value={outboundDrafts[outbound.id] ?? outbound.body}
                                        onChange={(event) =>
                                            setOutboundDrafts((values) => ({
                                                ...values,
                                                [outbound.id]: event.target.value,
                                            }))
                                        }
                                        multiline
                                        minRows={3}
                                        maxRows={6}
                                        sx={fieldSx}
                                    />
                                    <Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => void approveOutbound(outbound)}
                                            disabled={
                                                working ||
                                                profile?.status !== 'running' ||
                                                !access?.workspace?.launch.can_start ||
                                                (!selectedConversation.dialog_charge &&
                                                    !access?.workspace?.launch.can_start_new_dialog) ||
                                                !(outboundDrafts[outbound.id] ?? outbound.body).trim()
                                            }
                                        >
                                            Подтвердить и отправить
                                        </Button>
                                    </Box>
                                </Stack>
                            ))}
                        <Divider />
                        <Stack spacing={1}>
                            <TextField
                                label="Комментарий сотруднику"
                                value={handoffNote}
                                onChange={(event) => setHandoffNote(event.target.value)}
                                sx={fieldSx}
                            />
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                flexWrap="wrap"
                            >
                                {selectedConversation.operator_override?.active ? (
                                    <Button
                                        variant="outlined"
                                        onClick={() => void releaseConversation()}
                                        disabled={working}
                                    >
                                        Вернуть AI-менеджеру
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={() => void handoffConversation()}
                                        disabled={working}
                                    >
                                        Взять диалог себе
                                    </Button>
                                )}
                                <Button
                                    onClick={() => void finishConversation('goal_completed')}
                                    disabled={working}
                                >
                                    Цель достигнута
                                </Button>
                                <Button
                                    color="error"
                                    onClick={() => void finishConversation('stopped')}
                                    disabled={working}
                                >
                                    Остановить диалог
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </Paper>
        </Stack>
    );

    const telegram = () => (
        <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3">Telegram</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        Подключите свой аккаунт. Запуск и отправка не выполняются автоматически.
                    </Typography>
                </Box>
                <Chip
                    label={telegramLabel(profile?.telegram_status, access?.workspace?.launch.transport.code, true)}
                    color={
                        telegramLabel(profile?.telegram_status, access?.workspace?.launch.transport.code, true) ===
                        'Подключён'
                            ? 'success'
                            : 'default'
                    }
                    sx={{ alignSelf: 'flex-start' }}
                />
                {telegramAccounts.length > 0 && (
                    <ToggleButtonGroup
                        exclusive
                        value={telegramConnectionMode}
                        onChange={(_event, value: TelegramConnectionMode | null) => {
                            if (value) {
                                setTelegramConnectionMode(value);
                                setTelegramAuthNotice('');
                            }
                        }}
                        size="small"
                        aria-label="Способ подключения Telegram"
                    >
                        <ToggleButton value="existing">Подключённый Telegram</ToggleButton>
                        <ToggleButton value="new">Подключить другой аккаунт</ToggleButton>
                    </ToggleButtonGroup>
                )}
                {telegramConnectionMode === 'existing' && telegramAccounts.length > 0 ? (
                    <Stack spacing={1.5}>
                        {telegramAuthNotice && <Alert severity="error">{telegramAuthNotice}</Alert>}
                        <TextField
                            select
                            label="Аккаунт Telegram"
                            value={selectedTelegramClientId}
                            onChange={(event) => setSelectedTelegramClientId(Number(event.target.value))}
                            sx={fieldSx}
                        >
                            {telegramAccounts.map((account) => (
                                <MenuItem
                                    key={account.id}
                                    value={account.id}
                                >
                                    {account.label} · {account.ready ? 'готов' : 'нужна проверка'}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Box>
                            <Button
                                variant="contained"
                                onClick={() => void linkExistingTelegram()}
                                disabled={
                                    working ||
                                    !selectedTelegramClientId ||
                                    profile?.telegram_client === selectedTelegramClientId
                                }
                            >
                                {profile?.telegram_client === selectedTelegramClientId
                                    ? 'Аккаунт используется'
                                    : 'Использовать этот аккаунт'}
                            </Button>
                        </Box>
                    </Stack>
                ) : !telegramToken ? (
                    <Stack spacing={1.5}>
                        {telegramAuthNotice && <Alert severity="error">{telegramAuthNotice}</Alert>}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                        >
                            <TextField
                                fullWidth
                                label="Номер Telegram"
                                type="tel"
                                autoComplete="tel"
                                placeholder="+7 999 000-00-00"
                                value={phone}
                                onChange={(event) => setPhone(event.target.value)}
                                sx={{ ...fieldSx, minWidth: 0, flex: 1 }}
                            />
                            <Button
                                variant="contained"
                                onClick={startTelegram}
                                disabled={working || !phone.trim()}
                                sx={{
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    height: 'auto',
                                    minHeight: 48,
                                }}
                            >
                                {telegramAuthNotice ? 'Получить новый код' : 'Получить код'}
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack spacing={1.5}>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                        >
                            {telegramStage === 'password'
                                ? 'Введите пароль 2FA для этого аккаунта.'
                                : 'Введите код, который отправил Telegram.'}
                        </Typography>
                        {telegramStage === 'password' ? (
                            <TextField
                                label="Пароль 2FA"
                                type="password"
                                autoComplete="off"
                                value={telegramPassword}
                                onChange={(event) => setTelegramPassword(event.target.value)}
                                sx={fieldSx}
                            />
                        ) : (
                            <TextField
                                label="Код из Telegram"
                                type="text"
                                autoComplete="one-time-code"
                                value={telegramCode}
                                onChange={(event) => setTelegramCode(event.target.value)}
                                inputProps={{ inputMode: 'numeric' }}
                                sx={fieldSx}
                            />
                        )}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                        >
                            <Button
                                variant="contained"
                                onClick={verifyTelegram}
                                disabled={
                                    working || (telegramStage === 'code' ? !telegramCode.trim() : !telegramPassword)
                                }
                            >
                                Подтвердить
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => resetTelegramAuth(true)}
                                disabled={working}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );

    const goToWizardStep = (step: number) => {
        if (working) return;
        const nextStep = Math.max(0, Math.min(step, wizardSteps.length - 1));
        setWizardStep(nextStep);
    };

    const wizardContent = () => {
        if (wizardStep === 0) return openingStep();
        if (wizardStep === 1) return goalStep();
        if (wizardStep === 2) return companyStep();
        return knowledge();
    };

    const wizardProgress = () => (
        <Paper
            sx={{
                ...cardSx,
                p: 2,
                borderRadius: '16px',
                position: { md: 'sticky' },
                top: { md: 24 },
            }}
        >
            <Stack spacing={1.5}>
                <Stack
                    spacing={1.5}
                    alignItems="center"
                    textAlign="center"
                >
                    <AIAgentOrb
                        size={144}
                        state="idle"
                    />
                    <Typography
                        variant="h3"
                        sx={{ overflowWrap: 'anywhere' }}
                    >
                        {String(draft.name || profile?.name || 'AI-менеджер')}
                    </Typography>
                </Stack>
                <Divider />
                <Box>
                    <Typography fontWeight={800}>Подготовка менеджера</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.25}
                    >
                        Шаг {wizardStep + 1} из {wizardSteps.length}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        height: 6,
                        bgcolor: '#E8EEF8',
                        borderRadius: 99,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: `${((wizardStep + 1) / wizardSteps.length) * 100}%`,
                            height: '100%',
                            bgcolor: '#0044FF',
                        }}
                    />
                </Box>
                <Stack spacing={0.75}>
                    {wizardSteps.map((step, index) => {
                        const active = index === wizardStep;
                        return (
                            <Button
                                key={step.title}
                                color="inherit"
                                onClick={() => goToWizardStep(index)}
                                disabled={working}
                                aria-current={active ? 'step' : undefined}
                                sx={{
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    gap: 1.25,
                                    px: 1,
                                    py: 1,
                                    height: 'auto',
                                    minHeight: 54,
                                    border: '1px solid',
                                    borderColor: active ? '#0044FF' : 'var(--lm-border)',
                                    bgcolor: active ? '#F3F7FF' : 'var(--lm-surface)',
                                    '&:hover': {
                                        bgcolor: active ? '#EEF4FF' : 'var(--lm-canvas)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        bgcolor: active ? '#0044FF' : '#E8EEF3',
                                        color: active ? '#fff' : '#64748B',
                                        fontWeight: 800,
                                    }}
                                >
                                    {index + 1}
                                </Box>
                                <Box minWidth={0}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={active ? 800 : 700}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="secondary.main"
                                    >
                                        {step.caption}
                                    </Typography>
                                </Box>
                            </Button>
                        );
                    })}
                </Stack>
            </Stack>
        </Paper>
    );

    const wizard = () => (
        <Stack
            ref={wizardTopRef}
            spacing={2}
            sx={{ scrollMarginTop: { xs: 72, md: 24 } }}
        >
            <Paper sx={{ ...cardSx, p: 2, display: { xs: 'block', md: 'none' } }}>
                <Stack spacing={1}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography fontWeight={800}>{wizardSteps[wizardStep].title}</Typography>
                        <Chip
                            size="small"
                            label={`Шаг ${wizardStep + 1} из ${wizardSteps.length}`}
                        />
                    </Stack>
                    <Box
                        sx={{
                            height: 6,
                            bgcolor: '#E8EEF8',
                            borderRadius: 99,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                width: `${((wizardStep + 1) / wizardSteps.length) * 100}%`,
                                height: '100%',
                                bgcolor: '#0044FF',
                            }}
                        />
                    </Box>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Шаг настройки"
                        value={wizardStep}
                        onChange={(event) => goToWizardStep(Number(event.target.value))}
                        disabled={working}
                        sx={fieldSx}
                    >
                        {wizardSteps.map((step, index) => (
                            <MenuItem
                                key={step.title}
                                value={index}
                            >
                                {index + 1}. {step.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Paper>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        md: 'minmax(0, 1fr) 280px',
                    },
                    gap: 2,
                    alignItems: 'start',
                }}
            >
                <Stack
                    spacing={2}
                    minWidth={0}
                >
                    {wizardContent()}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={1}
                        sx={{ pr: { xs: 8, md: 0 } }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => goToWizardStep(wizardStep - 1)}
                            disabled={working || wizardStep === 0}
                        >
                            Назад
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void advanceWizard()}
                            disabled={working}
                        >
                            {wizardStep < wizardSteps.length - 1 ? 'Далее' : 'Проверить и подключить'}
                        </Button>
                    </Stack>
                </Stack>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>{wizardProgress()}</Box>
            </Box>
        </Stack>
    );

    const managerList = () => (
        <Stack spacing={2}>
            <Paper sx={{ ...cardSx, p: { xs: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                >
                    <Box>
                        <Typography
                            variant="body2"
                            color="secondary.main"
                        >
                            Общий баланс:{' '}
                            {billing?.total_dialog_credits_available ??
                                access?.workspace?.launch.credits_available ??
                                0}{' '}
                            диалогов
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" onClick={() => setBillingDialogOpen(true)}>
                            Пополнить диалоги
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void createManager()}
                            disabled={working || !access?.can_create}
                        >
                            Создать менеджера
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
            {managerProfiles.length === 0 ? (
                <Paper sx={{ ...cardSx, p: 3 }}>
                    <Typography variant="h3">Менеджеров пока нет</Typography>
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        mt={0.5}
                    >
                        Создайте первого менеджера и заполните четыре простых шага.
                    </Typography>
                </Paper>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                    }}
                >
                    {managerProfiles.map((manager) => {
                        const template = goalTemplates.find(
                            (item) => item.value === (manager.goal_template || 'custom')
                        );
                        const intake = manager.intake_options;
                        const selectedSources =
                            (intake?.selected.lead_stream_ids.length || 0) +
                            (intake?.selected.subscription_project_ids.length || 0);
                        return (
                            <Paper
                                key={manager.id}
                                sx={{ ...cardSx, p: 2.5 }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        gap={1}
                                    >
                                        <Typography variant="h3">{manager.name}</Typography>
                                        <Chip
                                            size="small"
                                            label={manager.status_label || manager.status}
                                        />
                                    </Stack>
                                    <Typography
                                        variant="body2"
                                        color="secondary.main"
                                    >
                                        Цель: {template?.label || 'Своя цель'}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="secondary.main"
                                    >
                                        Источники: {selectedSources ? `выбрано ${selectedSources}` : 'не выбраны'}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => requestSelection(manager.id)}
                                        disabled={working}
                                    >
                                        Открыть менеджера
                                    </Button>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Box>
            )}
        </Stack>
    );

    const launchSection = () => (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h2">Тест и запуск</Typography>
                <Typography
                    variant="body2"
                    color="secondary.main"
                    mt={0.5}
                >
                    Выберите источники, режим отправки, подключите Telegram и примите тест.
                </Typography>
            </Box>
            {sourceSettings()}
            {deliveryMode()}
            {telegram()}
            {test()}
            {overview()}
            <Button
                variant="outlined"
                onClick={() => setShowAdvanced((value) => !value)}
            >
                {showAdvanced ? 'Скрыть дополнительные настройки' : 'Дополнительные настройки'}
            </Button>
            {showAdvanced && (
                <Stack spacing={2}>
                    {advancedScenario()}
                    {limitsSettings()}
                </Stack>
            )}
        </Stack>
    );

    const renderSection = () => {
        if (section === 'list') return managerList();
        if (!profile) return <Alert severity="warning">Профиль менеджера не найден. Вернитесь к списку.</Alert>;
        if (section === 'dialogs') return dialogs();
        if (section === 'launch') return launchSection();
        return wizard();
    };

    const page = (
        <Box
            maxWidth={1040}
            mx="auto"
            width="100%"
            px={{ xs: 2, md: 0 }}
            py={{ xs: 2, md: 0 }}
        >
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h1">
                        {section === 'list' ? 'AI-менеджеры' : profile?.name || 'AI-менеджер'}
                    </Typography>
                </Box>
                {loading ? (
                    <Box
                        py={8}
                        textAlign="center"
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : loadFailed ? (
                    <Alert
                        severity="error"
                        action={
                            <Button
                                color="inherit"
                                onClick={() => void (profile ? openProfile(profile.id) : loadList())}
                            >
                                Повторить
                            </Button>
                        }
                    >
                        Не удалось загрузить данные.
                    </Alert>
                ) : access?.state !== 'granted' ? (
                    locked()
                ) : (
                    <>
                        {profile && section !== 'list' && (
                            <Paper sx={{ ...cardSx, p: 1 }}>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="wrap"
                                >
                                    {(
                                        [
                                            ['wizard', 'Настройка'],
                                            ['launch', 'Тест и запуск'],
                                            ['dialogs', 'Диалоги'],
                                        ] as Array<[Section, string]>
                                    ).map(([value, label]) => (
                                        <Button
                                            key={value}
                                            color={section === value ? 'primary' : 'inherit'}
                                            variant={section === value ? 'contained' : 'text'}
                                            onClick={() => setSection(value)}
                                            sx={{ boxShadow: 'none' }}
                                        >
                                            {label}
                                        </Button>
                                    ))}
                                    <Button
                                        color="inherit"
                                        variant="text"
                                        onClick={() => requestSelection('list')}
                                        disabled={working}
                                    >
                                        К списку
                                    </Button>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Stack direction="row" alignItems="center" spacing={1} px={1}>
                                        <Typography variant="body2" color="secondary.main">
                                            {billing?.total_dialog_credits_available ??
                                                access.workspace?.launch.credits_available ??
                                                0}{' '}
                                            диалогов
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setBillingDialogOpen(true)}
                                        >
                                            Пополнить диалоги
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}
                        {renderSection()}
                    </>
                )}
            </Stack>
            <DialogPackageCheckout
                open={billingDialogOpen}
                product="leadsmania"
                balance={
                    billing?.total_dialog_credits_available ?? access?.workspace?.launch.credits_available ?? 0
                }
                onClose={() => setBillingDialogOpen(false)}
                onBillingChange={setBilling}
            />
            <Dialog
                open={pendingSelection !== null}
                onClose={() => setPendingSelection(null)}
            >
                <DialogTitle>Есть несохранённые изменения</DialogTitle>
                <DialogContent>
                    <Typography>При переходе к другому менеджеру локальные изменения будут отменены.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingSelection(null)}>Остаться</Button>
                    <Button
                        color="error"
                        onClick={() => pendingSelection !== null && performSelection(pendingSelection)}
                    >
                        Перейти без сохранения
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    return <DesktopLayout hideRightSidebar>{page}</DesktopLayout>;
}
